import { useEffect, useId, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

// The nav's audience switch: Find work | Get cover.
//
// Monzo's Personal/Business pill, and the same job: tell a visitor which half of
// the marketplace they are reading, and let them switch in one click. Three
// things make it that control rather than two words (founder 2026-10-02, from
// the Monzo capture):
//
//   · an ICON per side, so the two are distinguishable before they are read
//   · a CHEVRON on the active side, because the active side is a MENU
//   · the trunk's own pages live in that menu
//
// That last one is why Monzo's header carries no other links: the pill is the
// navigation. Ours followed suit, so the bar is wordmark, pill, sign in, action,
// and it stays that way when a trunk gains a page.
//
// ⛔ The words. "Hire" was rejected by the founder and the reason is not
// cosmetic: the professionals are self employed and we never hire them. "Cover"
// is the house noun for what a workplace gets. Never hire, staff, temp or employ.
//
// ⛔ Both icons are MONO. The pill is chrome, and chrome is never the brand
// colour (the one accent rule the apps hold to).

const PersonIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...p}>
    <path d="M12 12a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Zm0 2c-4 0-7.5 2.2-7.5 5v1.2c0 .4.3.8.8.8h13.4c.5 0 .8-.4.8-.8V19c0-2.8-3.5-5-7.5-5Z" />
  </svg>
)
const CaseIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...p}>
    <path d="M9 5.5A2.5 2.5 0 0 1 11.5 3h1A2.5 2.5 0 0 1 15 5.5V6h3a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V9a3 3 0 0 1 3-3h3v-.5ZM10.8 6h2.4v-.5a.7.7 0 0 0-.7-.7h-1a.7.7 0 0 0-.7.7V6Z" />
  </svg>
)

const TRUNKS = [
  {
    to: '/', label: 'Find work', Icon: PersonIcon,
    menu: [
      { to: '/how-it-works', label: 'How it works', note: 'Set up once, then pick up work' },
      { to: '/getting-paid', label: 'Getting paid', note: 'When the money arrives, and what it costs' },
      { to: '/trust',        label: 'Trust',        note: 'What we check, and who holds your money' },
    ],
  },
  {
    to: '/business', label: 'Get cover', Icon: CaseIcon,
    menu: [
      { to: '/business/how-it-works', label: 'How it works', note: 'Post it, fill it, settle it' },
      { to: '/business/pricing',      label: 'Pricing',      note: 'You see the price before you post' },
      { to: '/business/compliance',   label: 'Compliance',   note: 'Checks we run on every professional' },
    ],
  },
]

export function rememberTrunk(to) {
  try { localStorage.setItem('td:trunk', to) } catch { /* private mode */ }
}

export default function AudiencePill({ dark = false, className = '' }) {
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)
  const menuId = useId()

  const business = pathname.startsWith('/business')
  const activeIdx = business ? 1 : 0

  useEffect(() => { setOpen(false) }, [pathname])

  // Close on Escape (returning focus to the trigger) and on a click outside.
  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key !== 'Escape') return
      setOpen(false)
      wrapRef.current?.querySelector('[aria-haspopup]')?.focus()
    }
    const onDown = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false) }
    window.addEventListener('keydown', onKey)
    window.addEventListener('pointerdown', onDown)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('pointerdown', onDown)
    }
  }, [open])

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <div className={`inline-flex items-center rounded-full p-1 ${dark ? 'bg-white/10' : 'bg-ink/[0.06]'}`}>
        {TRUNKS.map((t, i) => {
          const active = i === activeIdx
          const { Icon } = t

          // The active side is a menu button; the inactive side is a link that
          // switches trunk. Two different jobs, so two different elements.
          return active ? (
            <button
              key={t.to}
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-haspopup="true"
              aria-controls={menuId}
              className={`flex items-center gap-2 rounded-full py-2 pl-3.5 pr-3 text-[15px] font-semibold transition-colors ${
                dark ? 'bg-white text-ink' : 'bg-surface text-ink shadow-[0_1px_3px_rgba(12,24,41,0.10)]'
              }`}
            >
              <Icon className="h-[20px] w-[20px] text-ink-muted" />
              {t.label}
              <svg
                viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
                strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                className={`text-ink-muted transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
          ) : (
            <Link
              key={t.to}
              to={t.to}
              onClick={() => rememberTrunk(t.to)}
              className={`flex items-center gap-2 rounded-full px-3.5 py-2 text-[15px] font-semibold transition-colors ${
                dark ? 'text-white/70 hover:text-white' : 'text-ink/75 hover:text-ink'
              }`}
            >
              <Icon className="h-[20px] w-[20px] opacity-70" />
              {t.label}
            </Link>
          )
        })}
      </div>

      {open && (
        <div
          id={menuId}
          className="absolute left-0 top-[calc(100%+10px)] z-50 w-[320px] rounded-[16px] border border-hairline bg-surface p-2 shadow-[0_18px_50px_rgba(12,24,41,0.18)]"
        >
          <Link
            to={TRUNKS[activeIdx].to}
            className="block rounded-[12px] px-3 py-2.5 transition-colors hover:bg-ink/[0.04]"
          >
            <span className="block text-[15px] font-semibold text-ink">Overview</span>
            <span className="block text-[13px] text-ink-muted">
              {activeIdx === 0 ? 'Work when you want, paid when you’re done' : 'Fill the gap, skip the phone calls'}
            </span>
          </Link>
          {TRUNKS[activeIdx].menu.map((m) => (
            <Link
              key={m.to}
              to={m.to}
              className="block rounded-[12px] px-3 py-2.5 transition-colors hover:bg-ink/[0.04]"
            >
              <span className="block text-[15px] font-semibold text-ink">{m.label}</span>
              <span className="block text-[13px] text-ink-muted">{m.note}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
